import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { FaClipboardList, FaPaperPlane, FaSpinner, FaExclamationTriangle, FaCheckCircle } from 'react-icons/fa';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClinicalFormWidget = ({ userEmail }) => {
  const [pendingForms, setPendingForms] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [responses, setResponses] = useState({});
  const [fileUploads, setFileUploads] = useState({}); // Para guardar archivos pendientes de subir
  const [uploadingFile, setUploadingFile] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userEmail) {
      fetchPendingForms();
    }
  }, [userEmail]);

  const fetchPendingForms = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/clinical-forms/user/${userEmail}/pending`);
      if (response.data?.pending_forms) {
        setPendingForms(response.data.pending_forms);
      }
    } catch (error) {
      console.error('Error fetching clinical forms:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldId, value) => {
    setResponses(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  const handleCheckboxChange = (fieldId, option, checked) => {
    setResponses(prev => {
      const currentValues = prev[fieldId] || [];
      if (checked) {
        return { ...prev, [fieldId]: [...currentValues, option] };
      } else {
        return { ...prev, [fieldId]: currentValues.filter(v => v !== option) };
      }
    });
  };

  // Función para subir archivos al servidor
  const handleFileUpload = async (fieldId, file) => {
    if (!file) return;
    
    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('user_email', userEmail);
      formData.append('field_id', fieldId);
      
      const response = await axios.post(`${API}/clinical-forms/upload-file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data?.file_url) {
        // Guardar la URL del archivo en las respuestas
        setResponses(prev => ({
          ...prev,
          [fieldId]: response.data.file_url
        }));
        toast.success('Archivo subido correctamente');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Error al subir el archivo. Por favor intenta de nuevo.');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedForm) return;
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/clinical-forms/submit/${selectedForm.id}`, {
        responses
      });
      
      if (response.data?.success) {
        toast.success('Formulario enviado correctamente');
        
        if (response.data.has_red_flags) {
          toast.warning('Se detectaron señales de alarma. Tu entrenadora revisará tu caso con prioridad.', {
            duration: 6000
          });
        }
        
        // Refrescar lista
        fetchPendingForms();
        setSelectedForm(null);
        setResponses({});
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Error al enviar el formulario');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field, sectionId) => {
    const fieldKey = field.id;
    
    switch (field.field_type) {
      case 'checkbox':
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="text-sm font-medium text-white">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-gray-400">{field.description}</p>
            )}
            <div className="space-y-2 pl-2">
              {field.options.map((option, idx) => (
                <label key={idx} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={(responses[fieldKey] || []).includes(option)}
                    onChange={(e) => handleCheckboxChange(fieldKey, option, e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-600 bg-gray-700 text-pink-500"
                  />
                  <span className="text-sm text-gray-300">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
      
      case 'text':
      case 'number':
        return (
          <div key={fieldKey} className="space-y-1">
            <label className="text-sm font-medium text-white">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-gray-400">{field.description}</p>
            )}
            <Input
              type={field.field_type}
              value={responses[fieldKey] || ''}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className="bg-gray-700 border-gray-600 text-white"
              placeholder={field.label}
            />
          </div>
        );
      
      case 'textarea':
        return (
          <div key={fieldKey} className="space-y-1">
            <label className="text-sm font-medium text-white">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-gray-400">{field.description}</p>
            )}
            <Textarea
              value={responses[fieldKey] || ''}
              onChange={(e) => handleInputChange(fieldKey, e.target.value)}
              className="bg-gray-700 border-gray-600 text-white min-h-[80px]"
              placeholder={field.label}
            />
          </div>
        );
      
      case 'file':
        return (
          <div key={fieldKey} className="space-y-1">
            <label className="text-sm font-medium text-white">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-gray-400">{field.description}</p>
            )}
            <div className="flex flex-col gap-2">
              <Input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    handleFileUpload(fieldKey, file);
                  }
                }}
                disabled={uploadingFile}
                className="bg-gray-700 border-gray-600 text-white file:mr-4 file:py-1 file:px-2 file:rounded file:border-0 file:text-sm file:bg-pink-500 file:text-white"
              />
              {uploadingFile && (
                <div className="flex items-center gap-2 text-pink-400 text-sm">
                  <FaSpinner className="animate-spin" /> Subiendo archivo...
                </div>
              )}
              {responses[fieldKey] && !uploadingFile && (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <FaCheckCircle /> Archivo subido: {responses[fieldKey].split('/').pop()}
                </div>
              )}
            </div>
          </div>
        );
      
      case 'radio':
        return (
          <div key={fieldKey} className="space-y-2">
            <label className="text-sm font-medium text-white">
              {field.label}
              {field.required && <span className="text-red-400 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-gray-400">{field.description}</p>
            )}
            <div className="space-y-2 pl-2">
              {field.options.map((option, idx) => (
                <label key={idx} className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name={fieldKey}
                    checked={responses[fieldKey] === option}
                    onChange={() => handleInputChange(fieldKey, option)}
                    className="mt-1 w-4 h-4 border-gray-600 bg-gray-700 text-pink-500 accent-pink-500"
                  />
                  <span className="text-sm text-gray-300">{option}</span>
                </label>
              ))}
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return null;
  }

  if (pendingForms.length === 0) {
    return null;
  }

  return (
    <div className="mb-6">
      {/* Alerta de formularios pendientes */}
      {!selectedForm && (
        <div className="bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-500/40 rounded-lg p-4">
          <div className="flex items-center gap-3">
            <FaClipboardList className="text-red-400 text-2xl" />
            <div className="flex-1">
              <h3 className="font-bold text-white">Formulario Clínico Pendiente</h3>
              <p className="text-sm text-gray-300">
                Tu entrenadora te ha enviado {pendingForms.length} formulario(s) para completar
              </p>
              {/* Mensaje explicativo sobre por qué debe llenar estos formularios */}
              <div className="mt-2 p-3 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                <p className="text-xs text-cyan-300">
                  <span className="font-semibold">¿Por qué debo llenar estos formularios?</span>
                </p>
                <p className="text-xs text-cyan-200/80 mt-1">
                  Basado en las condiciones de salud que indicaste en tu formulario inicial, 
                  necesitamos información adicional para personalizar tu plan de forma segura y efectiva.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setSelectedForm(pendingForms[0])}
              className="bg-pink-500 hover:bg-pink-600 text-white"
            >
              Completar Ahora
            </Button>
          </div>
        </div>
      )}

      {/* Modal de formulario */}
      {selectedForm && (
        <Card className="bg-gray-900 border-red-500/40">
          <CardHeader className="bg-gradient-to-r from-red-500/20 to-pink-500/20">
            <CardTitle className="text-white flex items-center gap-2">
              <FaClipboardList className="text-red-400" />
              {selectedForm.template?.name || 'Formulario Clínico'}
            </CardTitle>
            <CardDescription className="text-gray-300">
              {selectedForm.template?.description}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {selectedForm.template?.sections.map((section, idx) => (
              <div key={section.id} className="space-y-4">
                {/* Header de sección */}
                <div className={`rounded-lg p-3 ${section.title.includes('Alarma') ? 'bg-red-500/20 border border-red-500/40' : 'bg-gray-800'}`}>
                  <h4 className={`font-bold ${section.title.includes('Alarma') ? 'text-red-400' : 'text-pink-400'}`}>
                    {section.title}
                  </h4>
                  {section.description && (
                    <p className="text-sm text-gray-400 mt-1">{section.description}</p>
                  )}
                </div>
                
                {/* Campos de la sección */}
                <div className="space-y-4 pl-2">
                  {section.fields.map(field => renderField(field, section.id))}
                </div>
              </div>
            ))}

            {/* Botones de acción */}
            <div className="flex gap-3 pt-4 border-t border-gray-700">
              <Button
                onClick={() => {
                  setSelectedForm(null);
                  setResponses({});
                }}
                variant="outline"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-pink-500 hover:bg-pink-600 text-white"
              >
                {submitting ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" /> Enviando...
                  </>
                ) : (
                  <>
                    <FaPaperPlane className="mr-2" /> Enviar Formulario
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ClinicalFormWidget;